import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useRoutes, type Location } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { pageRoutes } from '@/router/pageRoutes';
import { jumpTo, scrollToEl } from '@/lib/smoothScroll';

gsap.registerPlugin(ScrollTrigger);

const EXIT = 0.18;
const ENTER = 0.38;
const keyOf = (l: Location) => `${l.pathname}${l.search}`;
const targets = () => Array.from(document.querySelectorAll<HTMLElement>('[data-route-fade]'));
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollMemory = new Map<string, number>();

export default function RouteTransition() {
  const location = useLocation();
  const navType = useNavigationType();
  const [display, setDisplay] = useState<Location>(location);
  const latest = useRef(location);
  const latestNav = useRef(navType);
  const exiting = useRef(false);
  const pendingEnter = useRef<null | 'PUSH' | 'POP' | 'REPLACE'>(null);
  const tween = useRef<gsap.core.Tween | null>(null);
  const displayRef = useRef(display);
  displayRef.current = display;

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Remember scroll position of the page we are on, per history entry.
  useEffect(() => {
    const save = () => scrollMemory.set(displayRef.current.key, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, []);

  useEffect(() => {
    latest.current = location;
    latestNav.current = navType;
    const cur = displayRef.current;
    if (location.key === cur.key) return;

    // Same page, only hash changed: no transition, let anchor scroll happen.
    if (keyOf(location) === keyOf(cur) && !exiting.current) {
      setDisplay(location);
      if (location.hash) {
        scrollToEl(document.getElementById(location.hash.slice(1)));
      }
      return;
    }

    if (exiting.current) return; // the running exit will pick up latest.current

    tween.current?.kill();
    const finish = () => {
      exiting.current = false;
      pendingEnter.current = latestNav.current;
      setDisplay(latest.current);
    };

    if (reduced()) {
      finish();
      return;
    }
    exiting.current = true;
    tween.current = gsap.to(targets(), {
      opacity: 0,
      duration: EXIT,
      ease: 'power1.out',
      overwrite: true,
      onComplete: finish,
    });
  }, [location, navType]);

  useLayoutEffect(() => {
    const nav = pendingEnter.current;
    if (!nav) return;
    pendingEnter.current = null;

    const els = targets();
    const saved = scrollMemory.get(display.key);
    if (nav === 'POP' && saved !== undefined) {
      ScrollTrigger.refresh();
      jumpTo(saved);
    } else if (display.hash) {
      jumpTo(0);
      requestAnimationFrame(() => scrollToEl(document.getElementById(display.hash.slice(1)), false));
    } else {
      jumpTo(0);
    }

    const rm = reduced();
    tween.current = gsap.fromTo(
      els,
      { opacity: 0, y: rm ? 0 : 8 },
      {
        opacity: 1,
        y: 0,
        duration: rm ? 0.12 : ENTER,
        ease: 'power3.out',
        overwrite: true,
        clearProps: 'opacity,transform',
        onComplete: () => {
          ScrollTrigger.refresh();
          if (nav === 'POP' && saved !== undefined) jumpTo(saved);
        },
      },
    );

    const main = document.querySelector<HTMLElement>('main[data-route-fade]');
    main?.focus({ preventScroll: true });
  }, [display]);

  useEffect(() => () => { tween.current?.kill(); }, []);

  return useRoutes(pageRoutes, display);
}